import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useTranslation } from "../../../localization/useTranslation";
import { Box, Typography } from "@mui/material";
import { useSharingsAgreementBoxHooks } from "./sharingsAgreementBox.hooks";
import AgreementDialog from "../agreementDialog/DataProcessorAgreementDialog";
import { SelectableAgreement, StepTypeEnum } from "../../ProcessingActivity.types";
import { DataProcessorAgreementSaveModel } from "../agreementDialog/DataProcessorAgreementDialog.types";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { Color, DotLegalDeleteDialog, DotLegalOverflowMenuItem, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useSharingsAgreeementStyles } from "./sharingsAgreementBox.styles";
import { isBool } from "../../../common/booleanOperations";
import DotLegalMultiSelectWithButton from "../../../common/components/dotLegalSelectWithButton/DotLegalMultiSelectWithButton";
import { Trans } from "react-i18next";
import { DocumentStatus } from "../../../documents/Documents.types.ts";
import React, { useState } from "react";
import EditLegalEntityDocumentDialog from "../../../legalEntity/legalEntityDocumentTab/editLegalEntityDocumentDIalog/EditLegalEntityDocumentDialog.tsx";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import TransferLegalEntityDocumentDialog from "../../../legalEntity/legalEntityDocumentTab/transferLegalEntityDocumentDialog/TransferLegalEntityDocumentDialog.tsx";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { DocumentTypeUsageType } from "../../../masterData/documentType/documentTypes/DocumentTypes.types.ts";

export interface ISharingsAgreementBox {
    showAddAgreementBox: boolean;
    getSelectedDocuments: string[];
    options: SelectableAgreement[];
    saveAgreement: (agreement: SelectableAgreement, dataprocessor: string) => void;
    id: string | undefined;
    disabled?: boolean;
    validationError?: string;
    onYesOrNoChange: (hasAgreement: boolean) => void;
    dataProcessorAgreementSaveModel: DataProcessorAgreementSaveModel | undefined;
    onAddNewAgreementClick: () => void;
    stepType: StepTypeEnum;
    singleSelect?: boolean | undefined;
    isLoading?: boolean;
    onAddedAgreement: (agreement: SelectableAgreement) => void;
    onDeletedAgreement: (agreementId: SelectableAgreement) => void;
    documentTypeUsageType: DocumentTypeUsageType;
    refetchDocuments: () => void;
    legalEntityId: string | undefined;
    isGroupEntity: boolean | undefined;
}

export default function SharingsAgreementBox(props: ISharingsAgreementBox) {
    const [selectedDocument, setSelectedDocument] = useState<string | undefined>(undefined);
    const navigate = useNavigate();
    const { getContractOverviewUrl } = useUrlProvider();
    const { permissions } = useUserContext();
    const { dataProtectionFeatures, addonsFeatures } = usePlanContext();
    const styles = useSharingsAgreeementStyles(!dataProtectionFeatures.dataProcessingAgreements);
    const { translateString } = useTranslation();
    const {
        showAddAgreementDialog,
        setShowAddAgreementDialog,
        removeDialog,
        setRemoveDialog,
        onRemoveAgreementClick,
        transferDocumentDialog,
        setTransferDocumentDialog,
    } = useSharingsAgreementBoxHooks(props);

    const onClickAgreementLink = dataProtectionFeatures.dataProcessingAgreements
        ? () => {
              props.onAddNewAgreementClick();
              setShowAddAgreementDialog(true);
          }
        : () => {};

    function resolveAgreement(agreement: string) {
        return props.options!.find((o) => o.id === agreement)!;
    }

    const multiSelectOptions: Array<DotLegalSelectOption> = props.options.map((o) => ({
        id: o.id,
        name: o.name,
        color: o.type === "document" ? Color.blue : Color.purple,
        fontStyle: o.type === "document" && o.status !== DocumentStatus.active ? "italic" : undefined,
        hoverText:
            o.type === "document"
                ? `${translateString("documentType")}: ${o.contractOrDocumentType}`
                : o.type === "contract"
                  ? `${isNullOrWhitespace(o.contractOrDocumentType) ? "" : `${translateString("contractType")}: ${o.contractOrDocumentType}`}`
                  : o.contractOrDocumentType,
    }));

    function onChipClick(clikedId: string) {
        const type = resolveAgreement(clikedId).type;
        if (type === "document") {
            setSelectedDocument(clikedId);
        }
        if (type === "contract") {
            navigate(getContractOverviewUrl(clikedId));
        }
    }

    let dataprocessorAgreementSelect = (
        <DotLegalMultiSelectWithButton
            chosenOptions={props.getSelectedDocuments}
            isLoading={props.isLoading}
            label={translateString("agreement")}
            disableItemRemoval={props.disabled}
            disableSearch={props.disabled}
            disableSelection={props.disabled}
            placeholder={translateString("agreement")}
            toolTipText={translateString("agreementHelperText")}
            singleSelect={props.singleSelect}
            errorText={props.validationError}
            options={multiSelectOptions}
            disableClearable
            noOptionsLabel={translateString("noOptions")}
            onChange={(options) => {
                if (props.singleSelect) {
                    const isBooleanValue = options.find((x) => isBool(x));
                    if (isBooleanValue) {
                        const hasAgreement = isBooleanValue.toLowerCase() === "true";

                        props.onYesOrNoChange(hasAgreement);
                    } else {
                        const agreement = options[0];
                        props.onAddedAgreement(resolveAgreement(agreement));
                    }
                }
            }}
            onChipClick={onChipClick}
            onItemAdded={props.singleSelect ? undefined : (item) => props.onAddedAgreement(resolveAgreement(item))}
            onItemDeleted={props.singleSelect ? undefined : (item) => onRemoveAgreementClick(item)}
            buttonLabel={translateString("createAgreement")}
            onButtonClick={onClickAgreementLink}
            hasPermission={props.showAddAgreementBox && dataProtectionFeatures.dataProcessingAgreements}
        />
    );

    const getAgreementLink = () => {
        const link = props.disabled ? (
            <></>
        ) : (
            <Typography onClick={onClickAgreementLink} sx={styles.descriptionLabel}>
                {translateString("createAgreement")}
            </Typography>
        );

        if (!dataProtectionFeatures.dataProcessingAgreements) {
            return <DotLegalTooltip text={translateString("upgradeDataProtectionPlan")}>{link}</DotLegalTooltip>;
        }

        return link;
    };

    function getTransferDocumentOverflowMenu() {
        if (!addonsFeatures.research || !permissions.companyDocumentationPermissions.edit) {
            return undefined;
        }

        let menuItems: Array<DotLegalOverflowMenuItem> = [];

        menuItems.push({
            menuItem: translateString("transferToAnotherLegalEntity"),
            onClick: () => {
                setTransferDocumentDialog({ showDialog: true, documentId: selectedDocument ?? "" });
                setSelectedDocument(undefined);
            },
        });

        return menuItems;
    }

    return (
        <>
            {props.showAddAgreementBox ? (
                <Box sx={styles.container}>
                    {dataprocessorAgreementSelect}
                    {getAgreementLink()}

                    {showAddAgreementDialog && !props.isLoading && !isNullOrWhitespace(props.id) && (
                        <AgreementDialog
                            stepType={props.stepType}
                            onDialogClose={() => setShowAddAgreementDialog(false)}
                            saveModel={props.dataProcessorAgreementSaveModel!}
                            onSave={(agreementId) =>
                                props.saveAgreement({ type: "document", id: agreementId, contractOrDocumentType: "", name: "" }, props.id!)
                            }
                            usageType={props.documentTypeUsageType}
                        />
                    )}
                </Box>
            ) : (
                <>{dataprocessorAgreementSelect}</>
            )}
            {removeDialog.showDialog && (
                <DotLegalDeleteDialog
                    open
                    onDialogClose={() => setRemoveDialog({ id: "", showDialog: false, name: "" })}
                    onOkClick={() => {
                        props.onDeletedAgreement(resolveAgreement(removeDialog.id));
                        setRemoveDialog({ id: "", showDialog: false, name: "" });
                    }}
                    headerText={translateString("removeAgreement")}
                    alertBodyText={<Trans i18nKey="removeAgreementInfo" values={{ agreement: removeDialog.name }}></Trans>}
                    warningTranslation={""}
                    deleteBtnText={translateString("remove")}
                    cancelBtnText={translateString("cancel")}
                />
            )}

            {selectedDocument && (
                <EditLegalEntityDocumentDialog
                    onCloseDialog={() => {
                        setSelectedDocument(undefined);
                    }}
                    documentId={selectedDocument!}
                    legalEntityId={props.legalEntityId!}
                    isGroupEntity={props.isGroupEntity!}
                    isExtended={false}
                    onSave={props.refetchDocuments}
                    stepType={StepTypeEnum.dataprocessor}
                    overFlowMenu={getTransferDocumentOverflowMenu()}
                    documentTypeUsageType={props.documentTypeUsageType}
                />
            )}

            {transferDocumentDialog.showDialog && (
                <TransferLegalEntityDocumentDialog
                    onDialogClose={() => setTransferDocumentDialog({ documentId: "", showDialog: false })}
                    legalEntityId={props.legalEntityId!}
                    documentId={transferDocumentDialog.documentId}
                    onTransfered={() => {
                        props.onDeletedAgreement(resolveAgreement(transferDocumentDialog.documentId!));
                        props.refetchDocuments();
                    }}
                    isGroupEntity={props.isGroupEntity!}
                />
            )}
        </>
    );
}
